import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { EditabilityBasedOnProcessStatus } from '../../entities/editabilityBasedOnProcessStatus';
import { EnvironmentService } from 'src/app/services/environment.service';
import { GridApplicationDocumentTypes } from '../../entities/enums/grid-application-document-types.enum';

@Component({
  selector: 'app-base-section',
  template: '',
})
export abstract class BaseSectionComponent extends DestroyableComponent {
  @Input() isDetailsView: boolean;
  @Input() applicationId: number;
  @Input() editabilityBasedOnProcessStatus: EditabilityBasedOnProcessStatus;

  @Output() lockUnlockButtonClick = new EventEmitter<boolean>();

  role: string = JSON.parse(localStorage.getItem('role'));
  isCommentVisible: boolean = false;
  documentTypes = GridApplicationDocumentTypes;

  constructor(protected environmentService: EnvironmentService) {
    super();
  }

  public get isSectionDisabled(): boolean {
    return this.isDetailsView && this.isDisabledRespectToRole() && this.sectionLocked !== true;
  }

  public showCommentTooltip(): boolean {
    return (this.isModeratorRole() && this.editabilityBasedOnProcessStatus.internalRoles) || this.isCommentVisible || !!this.sectionComment;
  }

  public isModeratorRole(): boolean {
    return this.role === 'employee' || this.role === 'administrator';
  }

  public isTechnicianRole(): boolean {
    return this.role === 'standard_technician' || this.role === 'guest_technician';
  }

  public isCustomerRole(): boolean {
    return this.role === 'customer';
  }

  public lockSection(): void {
    if (!this.isModeratorRole()) {
      return;
    }

    this.sectionLocked = this.sectionLocked ? null : true;

    this.lockUnlockButtonClick.emit(true);
  }

  public unlockSection(): void {
    if (!this.isModeratorRole()) {
      return;
    }

    if (this.sectionLocked === false) {
      this.sectionLocked = null;
      return;
    }

    this.sectionLocked = false;
    if (!this.isCommentVisible) {
      this.showComment();
    }

    this.lockUnlockButtonClick.emit(true);
  }

  public showComment(): void {
    if (!this.isModeratorRole() || !this.editabilityBasedOnProcessStatus.internalRoles) {
      return;
    }

    this.isCommentVisible = !(this.isCommentVisible && !this.sectionComment);
  }

  public async updateComment(data: { value: string }): Promise<void> {
    if (!this.isModeratorRole() || !this.editabilityBasedOnProcessStatus.internalRoles) {
      return;
    }

    this.sectionComment = data.value;

    await this.saveComment();
  }

  public async deleteComment(): Promise<void> {
    if (!this.isModeratorRole() || !this.editabilityBasedOnProcessStatus.internalRoles) {
      return;
    }

    this.sectionComment = null;

    await this.saveComment();
  }

  public getSectionComment = () => this.sectionComment;

  public getSectionLocked = () => this.sectionLocked;

  protected isDisabledRespectToRole(): boolean {
    if (this.role === 'observer') {
      return true;
    }

    if (this.isModeratorRole() && this.environmentService.shouldInternalRolesBeAbleToEditApplication) {
      return false;
    }

    if (this.isCustomerRole()) {
      return !this.editabilityBasedOnProcessStatus.customerRole;
    }

    if (this.isTechnicianRole()) {
      return !this.editabilityBasedOnProcessStatus.technicianRole;
    }

    return true;
  }

  protected abstract get sectionComment(): string | null;
  protected abstract set sectionComment(value: string | null);

  protected abstract get sectionLocked(): boolean | null;
  protected abstract set sectionLocked(value: boolean | null);

  protected abstract saveComment(): Promise<void>;
}
